import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '@/lib/enhancedAxiosInstance';
import { isAxiosError } from 'axios';
import { Family, FamilyInput } from '../types/family';
import { API_ENDPOINTS } from '@/config/config';
import { logError } from '@/utils/errorHandler';

// Query keys
export const familyKeys = {
  all: ['families'] as const,
  lists: () => [...familyKeys.all, 'list'] as const,
  list: (filters: any) => [...familyKeys.lists(), { filters }] as const,
  details: () => [...familyKeys.all, 'detail'] as const,
  detail: (id: number) => [...familyKeys.details(), id] as const,
};

// Error handling helper
const handleApiError = (error: unknown) => {
  if (isAxiosError(error)) {
    const axiosError = error;
    if (axiosError.response?.status === 403) {
      throw new Error('You do not have permission to perform this action');
    }
    if (axiosError.response?.data) {
      if (axiosError.response.data.detail) {
        throw new Error(axiosError.response.data.detail);
      }
      throw new Error(JSON.stringify(axiosError.response.data));
    }
  }
  throw error;
};

// API functions
export const getFamilies = async (): Promise<Family[]> => {
  try {
    const { data } = await axiosInstance.get(API_ENDPOINTS.families.list);
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getFamily = async (id: number): Promise<Family> => {
  try {
    const { data } = await axiosInstance.get(API_ENDPOINTS.families.update(id));
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const createFamily = async (familyData: Partial<FamilyInput>): Promise<Family> => {
  try {
    const { data } = await axiosInstance.post(API_ENDPOINTS.families.create, familyData);
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateFamily = async (
  id: number,
  familyData: Partial<FamilyInput>
): Promise<Family> => {
  try {
    const { data } = await axiosInstance.patch(API_ENDPOINTS.families.update(id), familyData);
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const deleteFamily = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(API_ENDPOINTS.families.delete(id));
  } catch (error) {
    return handleApiError(error);
  }
};

export const addAttributeGroupToFamily = async (
  familyId: number,
  payload: {
    attribute_group: number;
    required: boolean;
    order: number;
  }
): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(
      `/api/families/${familyId}/attribute-groups/`,
      payload
    );
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const removeAttributeGroupFromFamily = async (
  familyId: number,
  attributeGroupId: number
): Promise<void> => {
  try {
    await axiosInstance.delete(`/api/families/${familyId}/attribute-groups/${attributeGroupId}/`);
  } catch (error) {
    return handleApiError(error);
  }
};

// Override hooks
interface OverrideAttributeGroupParams {
  groupId: number;
  removed: boolean;
}

export interface FamilyOverridePayload {
  attribute_group: number;
  removed: boolean;
}

export const overrideAttributeGroup = async (
  productId: number,
  params: OverrideAttributeGroupParams
): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(`/api/products/${productId}/override-group/`, params);
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export interface AttributeOverridePayload {
  attribute: number;
  removed: boolean;
}

export const overrideFamilyGroups = async (
  productId: number,
  overrides: FamilyOverridePayload[]
): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(
      `/api/products/${productId}/family-overrides/`,
      overrides
    );
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const overrideAttributes = async (
  productId: number,
  overrides: AttributeOverridePayload[]
): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(
      `/api/products/${productId}/attribute-overrides/`,
      overrides
    );
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// React Query hooks
export const useFamilies = () => {
  return useQuery({
    queryKey: familyKeys.lists(),
    queryFn: getFamilies,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('permission')) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const useFamily = (id: number) => {
  return useQuery({
    queryKey: familyKeys.detail(id),
    queryFn: () => getFamily(id),
    enabled: !!id,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('permission')) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const useCreateFamily = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createFamily,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: familyKeys.lists() });
    },
    onError: (error: Error) => {
      logError(
        'Failed to create family',
        {
          component: 'familyApi',
          action: 'useCreateFamily',
          metadata: { errorMessage: error.message },
        },
        { showToast: false, userFacing: false }
      );
      return error;
    },
  });
};

export const useUpdateFamily = (id: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (familyData: Partial<FamilyInput>) => updateFamily(id, familyData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: familyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: familyKeys.detail(id) });
    },
    onError: (error: Error) => {
      logError(
        'Failed to update family',
        {
          component: 'familyApi',
          action: 'useUpdateFamily',
          metadata: { familyId: id, errorMessage: error.message },
        },
        { showToast: false, userFacing: false }
      );
      return error;
    },
  });
};

export const useDeleteFamily = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteFamily,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: familyKeys.lists() });
    },
    onError: (error: Error) => {
      logError(
        'Failed to delete family',
        {
          component: 'familyApi',
          action: 'useDeleteFamily',
          metadata: { errorMessage: error.message },
        },
        { showToast: false, userFacing: false }
      );
      return error;
    },
  });
};

export const useAddAttributeGroupToFamily = (familyId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { attribute_group: number; required: boolean; order: number }) =>
      addAttributeGroupToFamily(familyId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: familyKeys.detail(familyId) });
    },
    onError: (error: Error) => {
      logError(
        'Failed to add attribute group to family',
        {
          component: 'familyApi',
          action: 'useAddAttributeGroupToFamily',
          metadata: { familyId, errorMessage: error.message },
        },
        { showToast: false, userFacing: false }
      );
      return error;
    },
  });
};

export const useRemoveAttributeGroupFromFamily = (familyId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (attributeGroupId: number) =>
      removeAttributeGroupFromFamily(familyId, attributeGroupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: familyKeys.detail(familyId) });
    },
    onError: (error: Error) => {
      logError(
        'Failed to remove attribute group from family',
        {
          component: 'familyApi',
          action: 'useRemoveAttributeGroupFromFamily',
          metadata: { familyId, errorMessage: error.message },
        },
        { showToast: false, userFacing: false }
      );
      return error;
    },
  });
};

export const useOverrideFamilyGroups = (productId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (overrides: FamilyOverridePayload[]) => overrideFamilyGroups(productId, overrides),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
    },
    onError: (error: Error) => {
      logError(
        'Failed to override family groups',
        {
          component: 'familyApi',
          action: 'useOverrideFamilyGroups',
          metadata: { productId, errorMessage: error.message },
        },
        { showToast: false, userFacing: false }
      );
      return error;
    },
  });
};

export const useOverrideAttributeGroup = (productId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: OverrideAttributeGroupParams) => overrideAttributeGroup(productId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
    },
    onError: (error: Error) => {
      logError(
        'Failed to override attribute group',
        {
          component: 'familyApi',
          action: 'useOverrideAttributeGroup',
          metadata: { productId, errorMessage: error.message },
        },
        { showToast: false, userFacing: false }
      );
      return error;
    },
  });
};

export const useOverrideAttributes = (productId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (overrides: AttributeOverridePayload[]) => overrideAttributes(productId, overrides),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
    },
    onError: (error: Error) => {
      logError(
        'Failed to override attributes',
        {
          component: 'familyApi',
          action: 'useOverrideAttributes',
          metadata: { productId, errorMessage: error.message },
        },
        { showToast: false, userFacing: false }
      );
      return error;
    },
  });
};

// Enhanced families with usage information - now merges stats from backend
export const getFamiliesWithUsage = async (): Promise<Family[]> => {
  try {
    // Fetch families and stats in parallel
    const [families, statsResponse] = await Promise.all([
      getFamilies(),
      axiosInstance.get('/api/families/stats/').catch(err => {
        // Gracefully handle missing endpoint (return empty list so we keep UI working)
        logError(
          'Failed to load /api/families/stats/',
          {
            component: 'familyApi',
            action: 'getFamiliesWithUsage',
            metadata: { errorMessage: err instanceof Error ? err.message : String(err) },
          },
          { showToast: false, userFacing: false }
        );
        return { data: [] };
      }),
    ]);

    // Build a map family_id -> product_count from stats (if available)
    const statsArray: { family_id: number; product_count: number }[] = statsResponse.data || [];
    const productCountMap = new Map<number, number>();
    statsArray.forEach(item => {
      productCountMap.set(item.family_id, item.product_count);
    });

    // Enhance with usage data
    return families.map(family => {
      const productCount = productCountMap.get(family.id) ?? 0;
      return {
        ...family,
        usage: {
          productCount,
          attributeGroupCount: family.attribute_groups?.length || 0,
          hasValues: productCount > 0,
        },
      };
    });
  } catch (error) {
    logError(
      'Error getting families with usage',
      {
        component: 'familyApi',
        action: 'getFamiliesWithUsage',
        metadata: { error },
      },
      { showToast: false, userFacing: false }
    );
    throw error;
  }
};

// Enhanced hook with usage information using merged stats
export const useFamiliesWithUsage = () => {
  return useQuery({
    queryKey: [...familyKeys.lists(), 'withUsage'],
    queryFn: getFamiliesWithUsage,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('permission')) {
        return false;
      }
      return failureCount < 3;
    },
  });
};
